import type { AppEntryContext, AppEntryReturnDestination, MedicalDeviceStatus } from '@core/entities/AppEntryContext';
import type { NhsAppRuntimeContext } from '@core/entities/NhsAppRuntime';
import type { JourneyResponse } from '@core/entities/JourneyDraftState';
import { AppEntryGatewayError } from '@core/ports/AppEntryContextGateway';
import { reconnectFromNhsAppGuidancePoints, restartFromNhsAppGuidancePoints, safeGuidanceHeading } from './safeGuidance';

export interface AppEntryShellViewModel {
  recoveryMessage?: string;
  returnDestinations: AppEntryReturnDestination[];
  signedInDisplayName?: string;
  showChrome: boolean;
}

export interface HandoffReadyPageViewModel {
  kind: 'ready';
  awarenessPoints: string[];
  continueLabel: string;
  proxyNotice?: string;
  title: string;
}

export interface HandoffFailurePageViewModel {
  kind: 'failure';
  guidanceHeading: string;
  guidancePoints: string[];
  message: string;
  title: string;
}

export type HandoffPageViewModel = HandoffReadyPageViewModel | HandoffFailurePageViewModel;

export interface BeforeYouStartPageViewModel {
  privacyPolicyAvailable: boolean;
  restoredResponse?: string;
  title: string;
}

export interface LegalPageViewModel {
  heading: string;
  intro: string;
  summary: string;
}

export interface MedicalDeviceStatusViewModel {
  heading: string;
  summary: string;
  regulatoryStatus: string;
  providerName: string;
  intendedUse: string;
  supportText: string;
}

export class AppEntryPresenter {
  public presentShell(context: AppEntryContext, runtime: NhsAppRuntimeContext, recoveryMessage?: string): AppEntryShellViewModel {
    return {
      showChrome: runtime.showChrome,
      returnDestinations: context.returnDestinations,
      recoveryMessage: recoveryMessage || undefined,
      signedInDisplayName: context.signedInDisplayName ?? context.proxyContext.proxyDisplayName ?? undefined,
    };
  }

  public presentHandoff(context: AppEntryContext): HandoffReadyPageViewModel {
    const awarenessPoints = [
      'You are leaving the NHS App.',
      'You are entering a third party service provided by your GP surgery.',
      'This service is not designed or owned by the NHS.',
    ];

    return {
      kind: 'ready',
      title: context.title,
      awarenessPoints,
      continueLabel: 'Continue to Intelligent Navigator',
      proxyNotice: context.proxyContext.isProxy
        ? `You are continuing for ${context.proxyContext.patientDisplayName ?? 'the selected patient'} as an authorised proxy user.`
        : undefined,
    };
  }

  public presentHandoffFailure(error: unknown): HandoffFailurePageViewModel {
    if (error instanceof AppEntryGatewayError && error.code === 'session-required') {
      return {
        kind: 'failure',
        title: 'Start again from the NHS App',
        message: 'We could not confirm your NHS App session, so Intelligent Navigator cannot continue safely from this page.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...restartFromNhsAppGuidancePoints],
      };
    }

    if (error instanceof AppEntryGatewayError && error.code === 'offline') {
      return {
        kind: 'failure',
        title: 'Check your internet connection',
        message: 'Intelligent Navigator cannot continue because your internet connection appears to be unavailable.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...reconnectFromNhsAppGuidancePoints],
      };
    }

    return {
      kind: 'failure',
      title: 'Intelligent Navigator cannot continue',
      message: 'There is a problem loading the information needed to start this journey right now.',
      guidanceHeading: safeGuidanceHeading,
      guidancePoints: [...restartFromNhsAppGuidancePoints],
    };
  }

  public presentBeforeYouStart(context: AppEntryContext, response?: JourneyResponse): BeforeYouStartPageViewModel {
    return {
      title: 'Check your symptoms',
      privacyPolicyAvailable: Boolean(context.privacyPolicyUri),
      restoredResponse: response?.answer ?? undefined,
    };
  }

  public presentPrivacyPolicy(): LegalPageViewModel {
    return {
      heading: 'Privacy Policy',
      intro: 'This local page is the DT-02 placeholder access point for the provider Privacy Policy before the live triage journey begins.',
      summary: 'Use the back link to return to the previous step after reviewing this information.',
    };
  }

  public presentTermsOfService(): LegalPageViewModel {
    return {
      heading: 'Terms of Service',
      intro: 'This local page is the DT-02 placeholder access point for the provider Terms of Service before the live triage journey begins.',
      summary: 'Use the back link to return to the previous step after reviewing this information.',
    };
  }

  public presentMedicalDeviceStatus(context: AppEntryContext): MedicalDeviceStatusViewModel | undefined {
    return this.mapMedicalDeviceStatus(context.medicalDeviceStatus);
  }

  private mapMedicalDeviceStatus(status?: MedicalDeviceStatus | null): MedicalDeviceStatusViewModel | undefined {
    if (!status) {
      return undefined;
    }

    return {
      heading: status.heading,
      summary: status.summary,
      regulatoryStatus: status.regulatoryStatus,
      providerName: status.providerName,
      intendedUse: status.intendedUse,
      supportText: status.supportText,
    };
  }
}